import { useIntl } from 'react-intl';
import {
    Controller,
    type Control,
    type FieldValues,
    type Path
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useValidationMessage } from '../i18n/validationMessages';
import { useFormReadOnly } from './formReadOnly';

/**
 * "Aufnahmedatum" (recordedAt) date-time input, bound to a react-hook-form
 * field that stores an ISO-8601 string. Uses the MUI X DateTimePicker so the
 * format is always German (DD.MM.YYYY HH:mm, 24h) whatever the UI language —
 * the picker adapter locale is pinned to 'de' (see utils/formatDate).
 */
export default function RecordedAtField<T extends FieldValues>({
    control,
    name,
    disableFuture = false,
    labelId = 'common.recordedAt',
    readOnly,
    dateOnly = false
}: {
    control: Control<T>;
    name: Path<T>;
    /** Prevent picking a datetime in the future (e.g. an Aufnahmedatum). */
    disableFuture?: boolean;
    /** Label override — e.g. the Fund reuses recordedAt under "Funddatum". */
    labelId?: string;
    /**
     * Shown but not editable — a reader without the right to change it.
     * Defaults to the surrounding form's (see formReadOnly).
     */
    readOnly?: boolean;
    /**
     * The day alone, no time to enter — the Fund's Funddatum. Stored as that
     * day at 12:00 local time: still a full timestamp like every recordedAt,
     * and noon stays on the same calendar day in UTC, where a midnight would
     * slip to the day before for anything reading the stored text's date.
     */
    dateOnly?: boolean;
}) {
    const intl = useIntl();
    const formReadOnly = useFormReadOnly();
    const validationMessage = useValidationMessage();
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                const common = {
                    label: intl.formatMessage({ id: labelId }),
                    disableFuture,
                    readOnly: readOnly ?? formReadOnly,
                    value: field.value ? dayjs(field.value as string) : null,
                    slotProps: {
                        textField: {
                            inputRef: field.ref,
                            fullWidth: true,
                            variant: 'filled' as const,
                            onBlur: field.onBlur,
                            error: !!fieldState.error,
                            helperText: fieldState.error
                                ? validationMessage(fieldState.error.message)
                                : undefined
                        }
                    }
                };
                return dateOnly ? (
                    <DatePicker
                        {...common}
                        onChange={(next) =>
                            field.onChange(
                                next && next.isValid()
                                    ? next.startOf('day').hour(12).toISOString()
                                    : ''
                            )
                        }
                    />
                ) : (
                    <DateTimePicker
                        {...common}
                        ampm={false}
                        onChange={(next) =>
                            field.onChange(
                                next && next.isValid() ? next.toISOString() : ''
                            )
                        }
                    />
                );
            }}
        />
    );
}
